import { useState } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { CLES, CLES_DRIVERS, CLES_ORDER, DIFFICULTE } from "@/data/idrCles";
import { DRIVER_BY_KEY, scrollTop } from "@/components/exo/driversExoShared";

const color = (k) => DRIVER_BY_KEY[k]?.color || "#B58C67";

export const Nav = ({ onPrev, onNext, nextLabel = "Suivant", prevLabel = "Précédent", disabled, testid = "cles" }) => (
  <div className="flex items-center justify-between mt-8">
    {onPrev ? <button onClick={onPrev} data-testid={`${testid}-prev`} className="px-6 py-3 rounded-full border border-[var(--gold)] text-[var(--gold)] font-medium hover:bg-[var(--gold)] hover:text-white transition-colors">{prevLabel}</button> : <span />}
    <button onClick={onNext} disabled={disabled} data-testid={`${testid}-next`} className={`px-8 py-3 rounded-full font-medium shadow-sm transition-all ${disabled ? "bg-[var(--beige)] text-[var(--ink-soft)] cursor-not-allowed" : "bg-[var(--gold)] text-white hover:brightness-105"}`}>{nextLabel}</button>
  </div>
);

const Bullets = ({ title, items, c }) => (
  <div className="rounded-2xl bg-white/70 border border-[var(--border)] p-4">
    <p className="text-sm font-semibold mb-2" style={{ color: c }}>{title}</p>
    <ul className="space-y-1.5">{items.map((it) => <li key={it} className="flex gap-2 text-sm text-[var(--ink)]"><span className="mt-2 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: c }} />{it}</li>)}</ul>
  </div>
);

const Header = ({ cle, sub }) => (
  <div className="mb-6">
    <p className="text-xs uppercase tracking-[0.25em] text-[var(--gold-light)]">Clé {cle.num} sur 3 — {cle.title}</p>
    {sub && <p className="text-sm text-[var(--ink-soft)] mt-1">{sub}</p>}
  </div>
);

const Intro = ({ cle, onNext, onPrev }) => (
  <div className="animate-fade-up text-center" data-testid={`cles-intro-${cle.key}`}>
    <p className="text-xs uppercase tracking-[0.3em] text-[var(--gold-light)] mb-4">Clé {cle.num} sur 3</p>
    <h2 className="font-serif-title text-4xl sm:text-5xl text-[var(--gold)] mb-6">{cle.title}</h2>
    <p className="text-[var(--ink)] leading-relaxed max-w-xl mx-auto text-lg">{cle.intro}</p>
    <Nav onPrev={onPrev} onNext={onNext} nextLabel="Découvrir les drivers" />
  </div>
);

const DriverScreen = ({ cle, k, i, onNext, onPrev }) => {
  const d = CLES_DRIVERS[k];
  const c = color(k);
  const img = cle.key === "antidotes" ? d.antidoteImg : `/drivers/miroir-${k}.jpg`;
  return (
    <div className="animate-fade-up" data-testid={`cles-${cle.key}-${k}`}>
      <Header cle={cle} sub={`Driver ${i + 1} sur 5`} />
      <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 sm:p-8">
        <img src={img} alt="" className="w-full h-44 sm:h-60 object-cover rounded-2xl mb-5" data-testid={`cles-img-${cle.key}-${k}`} />
        <h2 className="font-serif-title text-3xl sm:text-4xl mb-4" style={{ color: c }}>{d.name}</h2>
        {cle.key === "definitions" && <p className="font-serif-title text-xl sm:text-2xl text-[var(--ink)] italic leading-relaxed">{d.formulation}</p>}
        {cle.key === "dimensions" && (
          <div className="grid gap-4 sm:grid-cols-3">
            <Bullets title="Points positifs" items={d.positifs} c={c} />
            <Bullets title="Points négatifs" items={d.negatifs} c={c} />
            <Bullets title="De quoi on se coupe en maintenant ce driver" items={d.coupure} c={c} />
          </div>
        )}
        {cle.key === "antidotes" && (
          <div>
            <span className="inline-flex items-center rounded-full px-4 py-1.5 text-sm text-white mb-3" style={{ background: c }}>Antidote</span>
            <p className="font-serif-title text-2xl sm:text-3xl text-[var(--ink)] leading-snug">{d.antidote}</p>
            <p className="text-sm text-[var(--ink-soft)] mt-2">Symbole : {d.antidoteSymbole}</p>
          </div>
        )}
      </div>
      <Nav onPrev={onPrev} onNext={onNext} nextLabel={i === 4 ? "Passer au classement" : "Suivant"} />
    </div>
  );
};

export const RankList = ({ ranking, onMove, hint, testid = "cles-rank" }) => (
  <ul className="space-y-2" data-testid={testid}>
    {ranking.map((k, i) => { const d = CLES_DRIVERS[k]; const c = color(k); return (
      <li key={k} data-testid={`${testid}-${k}`} className="flex items-center gap-3 rounded-2xl border border-[var(--border)] bg-[var(--sand)] p-3">
        <span className="flex items-center justify-center h-8 w-8 rounded-full text-white font-semibold shrink-0" style={{ background: c }} data-testid={`${testid}-pos-${k}`}>{i + 1}</span>
        <div className="flex-1 min-w-0"><div className="font-medium text-[var(--ink)]">{d.name}</div>{hint && <div className="text-xs text-[var(--ink-soft)] leading-snug">{hint(d)}</div>}</div>
        <div className="flex items-center gap-1">
          <button onClick={() => onMove(k, -1)} disabled={i === 0} aria-label="Monter" data-testid={`${testid}-up-${k}`} className={`h-8 w-8 rounded-lg flex items-center justify-center border ${i === 0 ? "border-[var(--border)] text-[var(--beige)] cursor-not-allowed" : "border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-white"}`}><ChevronUp size={18} /></button>
          <button onClick={() => onMove(k, 1)} disabled={i === ranking.length - 1} aria-label="Descendre" data-testid={`${testid}-down-${k}`} className={`h-8 w-8 rounded-lg flex items-center justify-center border ${i === ranking.length - 1 ? "border-[var(--border)] text-[var(--beige)] cursor-not-allowed" : "border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-white"}`}><ChevronDown size={18} /></button>
        </div>
      </li>); })}
  </ul>
);

const RankScreen = ({ cle, ranking, onMove, difficulties, setDifficulty, onPrev, onNext, sending }) => {
  const isLast = cle.key === "antidotes";
  const complete = !isLast || CLES_ORDER.every((k) => Number.isFinite(difficulties[k]));
  return (
    <div className="animate-fade-up" data-testid={`cles-rank-${cle.key}`}>
      <Header cle={cle} />
      <div className="rounded-3xl bg-white/60 border border-[var(--border)] p-6 sm:p-8 mb-6">
        <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-1">{cle.rankTitle}</h3>
        <p className="text-sm text-[var(--ink-soft)] mb-5">{cle.rankText}</p>
        <RankList ranking={ranking} onMove={onMove} hint={cle.key === "definitions" ? (d) => d.short : cle.key === "antidotes" ? (d) => `Antidote : ${d.antidoteShort}` : null} />
      </div>
      {isLast && (
        <div className="rounded-3xl bg-white/60 border border-[var(--border)] p-6 sm:p-8" data-testid="cles-difficulties">
          <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-1">Difficulté de chaque antidote au quotidien</h3>
          <p className="text-sm text-[var(--ink-soft)] mb-5">Pour chaque antidote, à quel point est-il difficile pour vous de le mettre en pratique aujourd'hui ?</p>
          <div className="space-y-4">
            {CLES_ORDER.map((k) => { const d = CLES_DRIVERS[k]; const c = color(k); return (
              <div key={k} className="rounded-2xl border-2 p-4" style={{ borderColor: c }} data-testid={`cles-diff-${k}`}>
                <p className="text-sm font-semibold mb-2" style={{ color: c }}>{d.name} — {d.antidoteShort}</p>
                <div className="grid grid-cols-5 gap-1.5">
                  {DIFFICULTE.map((lv) => { const on = difficulties[k] === lv.value; return (
                    <button key={lv.value} onClick={() => setDifficulty(k, lv.value)} data-testid={`cles-diff-${k}-${lv.value}`} aria-pressed={on} className={`rounded-xl px-1 py-2 text-[0.7rem] sm:text-xs leading-tight border transition-colors ${on ? "text-white" : "bg-white/70 text-[var(--ink)] border-[var(--border)] hover:border-[var(--gold)]"}`} style={on ? { background: c, borderColor: c } : undefined}>{lv.label}</button>); })}
                </div>
              </div>); })}
          </div>
        </div>
      )}
      <Nav onPrev={onPrev} onNext={onNext} disabled={!complete || sending} nextLabel={sending ? "Transmission…" : isLast ? "Terminer et transmettre" : "Confirmer et continuer"} />
      {!complete && <p className="text-center text-xs text-[var(--ink-soft)] mt-3">Merci d'évaluer la difficulté des cinq antidotes pour terminer.</p>}
    </div>
  );
};

const startFor = (step) => (step === "cle2-done" ? 2 : step === "cle1-done" ? 1 : 0);

export const IdrCles = ({ idr, onSave, onDone }) => {
  const r = idr?.rankings || {};
  const [cle, setCle] = useState(() => startFor(idr?.step));
  const [sub, setSub] = useState("intro");
  const [ranking, setRanking] = useState(() => r.antidotes || r.dimensions || r.definitions || CLES_ORDER);
  const [difficulties, setDifficulties] = useState(() => idr?.difficulties || {});
  const current = CLES[cle];
  const go = (fn) => { fn(); scrollTop(); };
  const move = (k, dir) => setRanking((prev) => { const a = [...prev]; const i = a.indexOf(k); const j = i + dir; if (j < 0 || j >= a.length) return prev; [a[i], a[j]] = [a[j], a[i]]; return a; });
  const [sending, setSending] = useState(false);
  const confirm = async () => {
    if (sending) return;
    const last = cle === CLES.length - 1;
    setSending(true);
    const ok = await onSave({ rankings: { [current.key]: ranking }, ...(last ? { difficulties, step: "done" } : { step: `cle${cle + 1}-done` }) });
    setSending(false);
    if (ok === false) return;
    if (last) go(() => onDone());
    else go(() => { setCle(cle + 1); setSub("intro"); });
  };

  if (sub === "intro") return <Intro cle={current} onNext={() => go(() => setSub(0))} onPrev={cle > 0 ? () => go(() => { setCle(cle - 1); setSub("rank"); }) : undefined} />;
  if (sub === "rank") return <RankScreen cle={current} ranking={ranking} onMove={move} difficulties={difficulties} setDifficulty={(k, v) => setDifficulties((p) => ({ ...p, [k]: v }))} onPrev={() => go(() => setSub(4))} onNext={confirm} sending={sending} />;
  return <DriverScreen cle={current} k={CLES_ORDER[sub]} i={sub} onPrev={() => go(() => setSub(sub === 0 ? "intro" : sub - 1))} onNext={() => go(() => setSub(sub === 4 ? "rank" : sub + 1))} />;
};
